import { useEffect, useState } from "react";
import {
  Links,
  LiveReload,
  Meta,
  Outlet,
  useLoaderData,
  useOutletContext,
  useSearchParams,
} from "@remix-run/react";
import { themeV2 } from "~/styles/theme";
import { getApiUrl, useStoreImages } from "@commons-monorepo/index";
import { FontEnum } from "~/styles/theme.type";
import { GlobalStylesWrapper } from "~/styles/context/global-styles-wrapper";
import { LazyNewsletter } from "~/modules/newsletter/components/newsletter/lazy-newsletter";
import { MetaFunction } from "@remix-run/node";
import { CartContextProvider } from "~/modules/cart/providers/cart.provider";
import { getUrlCanonical, getFontSource } from "@commons-monorepo/index";
import { ShareStore } from "~/modules/catalogue/components/share-store";
import EmptyState404 from "~/components/empty-states/404/empty-404";
import { AgeRestriction } from "~/components/age-restriction/age-restriction";
import Footer from "@components-monorepo/components/footer/footer-ssr";
import MainHeader from "@components-monorepo/components/main-header/main-header-ssr";
import { ITheme } from "@commons-monorepo/index";
import { Header } from "~/components/header";
import { AuthContextProvider } from "~/common/contexts/auth.provider";
import { useTranslation } from "react-i18next";
import Cookies from "js-cookie";

export const meta: MetaFunction = ({ matches }) => {
  const parentsData = matches[0]?.data as any;
  const storeMetadata = parentsData?.storeMetadata;
  const facebookMetadata = storeMetadata?.configuration?.facebookMetadata;
  const googleShoppingVerificationTag =
    storeMetadata?.configuration?.googleMetadata;
  const facebookMetaContent = facebookMetadata?.match(/content=["“](.*)["”]/i);
  const googleShoppingVerificationTagContent =
    googleShoppingVerificationTag?.match(/content=["“](.*)["”]/i);
  const resultMatchFacebook = facebookMetaContent && facebookMetaContent[1];
  const resultMatchGoogleShoppingVerificationTag =
    googleShoppingVerificationTagContent &&
    googleShoppingVerificationTagContent[1];
  const canonical = getUrlCanonical(parentsData);

  return [
    { name: "facebook-domain-verification", content: resultMatchFacebook },
    {
      name: "google-site-verification",
      content: resultMatchGoogleShoppingVerificationTag,
    },
    { tagName: "link", rel: "canonical", href: canonical },
  ];
};

export const shouldRevalidate = () => false;

export { categoriesLoader as loader } from "~/loaders/categories.loader";

export default function route() {
  const { t } = useTranslation();
  const { storeMetadata, userAddons, offStore, ...context } =
    useOutletContext<any>();
  const partnerWithPrivateDomain = context.partnerWithPrivateDomain;
  const { categoriesResponse } = useLoaderData<any>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { partnerProfilePicture } = useStoreImages(storeMetadata);
  const token = searchParams.get("token");
  const embedBlank = searchParams.get("embed-blank") !== null;
  const cover = storeMetadata?.image?.cover;
  const [fontFamily, setFontFamily] = useState<FontEnum>(
    (storeMetadata.typography as FontEnum) || FontEnum.inter,
  );
  const [isClient, setIsClient] = useState(false);
  const previewAgeRestrictionValue = searchParams.get(
    "previewAgeRestrictionValue",
  );
  const preview = searchParams.get("preview");
  const embedded = searchParams.get("embedded");
  const ageRestrictionIsEnabled =
    (searchParams.get("previewAgeRestriction") && previewAgeRestrictionValue) ||
    storeMetadata?.configuration?.adultConfiguration?.enabled;

  const onLogin = (data: any) => {};

  const onLoginError = () => {
    localStorage.removeItem("accessToken");
  };

  const setFont = (font: FontEnum) => {
    setFontFamily(font);
  };

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    if (preview && typeof window !== "undefined") {
      Cookies.set("preview", "true");
      if (!embedded) {
        searchParams.delete("preview");
        setSearchParams(searchParams);
      }
    }
  }, []);

  return (
    <CartContextProvider storeMetadata={storeMetadata} offStore={offStore}>
      <GlobalStylesWrapper setFont={setFont} theme={context.theme}>
        <link href={`${getFontSource(fontFamily)}`} rel='stylesheet' />
        <AuthContextProvider
          url={`${getApiUrl()}/api/ms/user/customer-detail`}
          callback={onLogin}
          onError={onLoginError}
        >
          {!token && ageRestrictionIsEnabled && !embedBlank && (
            <AgeRestriction
              cover={cover?.coverImage}
              age={
                previewAgeRestrictionValue ||
                storeMetadata?.configuration?.adultConfiguration?.age ||
                "18"
              }
              name={storeMetadata?.enterpriseName}
              profilePic={partnerProfilePicture}
              isEnabled={ageRestrictionIsEnabled}
            />
          )}
          {!embedBlank && (
            <Header
              userAddons={userAddons}
              categoriesResponse={categoriesResponse}
              storeMetadata={storeMetadata}
              partnerWithPrivateDomain={partnerWithPrivateDomain}
            />
          )}
          <Outlet
            context={{
              ...categoriesResponse,
              ...context,
              userAddons,
              storeMetadata,
            }}
          />
          <LazyNewsletter />
          {isClient && <ShareStore />}
          {!embedBlank && (
            <Footer
              theme={context.theme}
              data={{
                storeMetadata,
                userAddons,
                hasOffers: categoriesResponse.hasOffers,
                hasWholesale: categoriesResponse.hasWholesale,
                hasServices: categoriesResponse.hasServices,
                storeUseSubdomain: context.storeUseSubdomain,
                url: context.url,
                categories: categoriesResponse.categories,
              }}
              t={t}
            />
          )}
        </AuthContextProvider>
      </GlobalStylesWrapper>
    </CartContextProvider>
  );
}

export function CatchBoundary() {
  const { t } = useTranslation();

  return (
    <html lang='es'>
      <head>
        <Meta />
        <Links />
        {typeof document === "undefined" ? "__STYLES__" : null}
      </head>
      <body>
        <GlobalStylesWrapper setFont={() => {}} theme={themeV2}>
          <MainHeader
            notFound
            categories={[]}
            hasOffers={false}
            hasServices={false}
            hasWholesale={false}
            isPrime={false}
            theme={{} as ITheme}
            ownerId={""}
            partnerId={""}
            data={{} as any}
            isPortraitInView={false}
            partnerWithPrivateDomain={false}
            t={t}
            goToCheckout={() => {}}
          />
          <EmptyState404 />
        </GlobalStylesWrapper>
        <LiveReload />
      </body>
    </html>
  );
}
